import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, mergeMap } from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  LoginOAuthResponse,
  SwapAuthForAccessTokens
} from '../../../models';
import {
  checkRequestInput,
  checkRequestIsDefined,
  getResponseId,
  isCompletedStatus,
  isUserNamePasswordRequired,
  returnAuthorizationResponseCode
} from '../login-remember-me-utils-service/initiate-login-utils';
import { InitiateLoginFacadeHelperService } from './initiate-login-facade-helper.service';

@Injectable({
  providedIn: 'root'
})
export class InitiateLoginFacadeService extends InitiateLoginFacadeHelperService {
  initiateLogin(
    loginOAuthParams: LoginOAuthParams,
    requestInput?: ILoginUserNamePasswordInput,
    appToken = false,
    eamAuthStateValue?: string
  ): Observable<AuthAccessTokenDetails | boolean | string> {
    this.postWithCredentialsFacade.pkceCode = loginOAuthParams.pkceCode;
    return this.loginAuthorization
      .loginAuthorization(loginOAuthParams, eamAuthStateValue)
      .pipe(
        concatMap((response: LoginOAuthResponse) => {
          this.postWithCredentialsFacade.flowId = getResponseId(response);
          if (
            response.status === loginOAuthParams.oauthConfig.IS_USER_REMEMBERED
          ) {
            return this.loginPostWithFingerPrint.loginPostWithFingerPrint(
              loginOAuthParams.oauthConfig,
              response?.id ? response.id : ''
            );
          } else if (
            response.status === loginOAuthParams.oauthConfig.COMPLETED
          ) {
            return this.loginRememberMeUtils.swapAuthCodeForToken(
              loginOAuthParams,
              response.authorizeResponse?.code
            );
          } else {
            return throwError(response);
          }
        }),
        concatMap(
          (
            response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens
          ) => {
            if (isUserNamePasswordRequired(response, loginOAuthParams)) {
              return checkRequestInput(
                loginOAuthParams,
                response,
                this.postWithCredentialsFacade,
                checkRequestIsDefined(requestInput)
              );
            } else if ('access_token' in response) {
              return of(response);
            } else if (isCompletedStatus(response, loginOAuthParams)) {
              return this.loginRememberMeUtils.swapAuthCodeForToken(
                loginOAuthParams,
                returnAuthorizationResponseCode(response)
              );
            } else {
              return throwError(response);
            }
          }
        ),
        concatMap(
          (
            response:
              | LoginAuthWithCredentialsResponse
              | SwapAuthForAccessTokens
              | AuthAccessTokenDetails
              | string
              | boolean
          ) => {
            return this.processResponse(response, loginOAuthParams, appToken);
          }
        ),
        catchError((error) => {
          if (typeof error === 'string') {
            return of(error);
          } else {
            return timer(
              loginOAuthParams.oauthConfig.LOGIN_RETRY_TIMER
                ? loginOAuthParams.oauthConfig.LOGIN_RETRY_TIMER
                : 1000
            ).pipe(
              mergeMap(() => {
                this.retryCount = this.retryCount + 1;
                if (
                  this.retryCount !==
                  loginOAuthParams.oauthConfig.LOGIN_RETRY_COUNT
                    ? loginOAuthParams.oauthConfig.LOGIN_RETRY_COUNT
                    : 0
                ) {
                  loginOAuthParams.pkceCode =
                    this.loginRememberMeUtils.generateCodeChallenge();
                  return this.initiateLogin(loginOAuthParams, requestInput);
                } else {
                  this.retryCount = 0;
                  return of(
                    this.loginRememberMeErrorUtil.checkRememberMeErrors(
                      loginOAuthParams.oauthConfig,
                      error
                    )
                  );
                }
              })
            );
          }
        })
      );
  }
}
